import { useEffect, useRef } from 'react';
import gsap from 'gsap';

// Soft gradient orbs that follow the cursor — no canvas, no dither.
// Opacity is kept low (≤0.18) so they never compete with text.
const CursorBackground = () => {
  const orb1Ref = useRef(null);
  const orb2Ref = useRef(null);
  const orb3Ref = useRef(null);

  useEffect(() => {
    const o1 = orb1Ref.current;
    const o2 = orb2Ref.current;
    const o3 = orb3Ref.current;
    if (!o1 || !o2 || !o3) return;

    // quickTo: different durations create a parallax lag feel
    const x1 = gsap.quickTo(o1, 'x', { duration: 0.7, ease: 'power2.out' });
    const y1 = gsap.quickTo(o1, 'y', { duration: 0.7, ease: 'power2.out' });
    const x2 = gsap.quickTo(o2, 'x', { duration: 1.1, ease: 'power2.out' });
    const y2 = gsap.quickTo(o2, 'y', { duration: 1.1, ease: 'power2.out' });
    const x3 = gsap.quickTo(o3, 'x', { duration: 1.8, ease: 'power1.out' });
    const y3 = gsap.quickTo(o3, 'y', { duration: 1.8, ease: 'power1.out' });

    const onMove = (e) => {
      x1(e.clientX - 250); y1(e.clientY - 250);
      x2(e.clientX - 200); y2(e.clientY - 200);
      x3(e.clientX - 300); y3(e.clientY - 300);
    };

    window.addEventListener('mousemove', onMove, { passive: true });

    // Idle: position orbs at visually pleasing spots until the mouse moves
    gsap.set(o1, { x: window.innerWidth * 0.3 - 250, y: window.innerHeight * 0.4 - 250 });
    gsap.set(o2, { x: window.innerWidth * 0.6 - 200, y: window.innerHeight * 0.5 - 200 });
    gsap.set(o3, { x: window.innerWidth * 0.5 - 300, y: window.innerHeight * 0.3 - 300 });

    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const base = {
    position: 'absolute',
    top: 0,
    left: 0,
    borderRadius: '50%',
    pointerEvents: 'none',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Primary orb — blue-grey, close cursor follow */}
      <div
        ref={orb1Ref}
        style={{
          ...base,
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(116,139,145,0.18) 0%, transparent 70%)',
          filter: 'blur(55px)',
        }}
      />
      {/* Accent orb — orange, medium lag */}
      <div
        ref={orb2Ref}
        style={{
          ...base,
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(254,91,0,0.13) 0%, transparent 70%)',
          filter: 'blur(70px)',
        }}
      />
      {/* Secondary orb — blue-grey, slow drift */}
      <div
        ref={orb3Ref}
        style={{
          ...base,
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(116,139,145,0.08) 0%, transparent 70%)',
          filter: 'blur(90px)',
        }}
      />
    </div>
  );
};

export default CursorBackground;
